import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FaSearch,
  FaMoneyBillWave,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaTimes,
} from "react-icons/fa";
import { Row, Col } from "react-bootstrap";
import Pagination from "../../../components/general/pagination/Pagination";
import ActionDropdown from "../../../components/admin/dropdown/action/ActionDropdown";
import StatCard from "../../../components/admin/statcard/StatCard";
import AdminLayout from "../../../layouts/AdminLayout";
import MyContext from "../../../context/MyContext";
import "../../../assets/css/admin/topup/DeliveryTopup.css";
import { DeliveryTopupSkeleton } from "../../../components/general/skeleton/SkeletonBlock";

const DeliveryTopup = () => {
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, authToken } = useContext(MyContext);
  const [loading, setLoading] = useState(true);
  const [topupList, setTopupList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState("all");
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTopup, setSelectedTopup] = useState(null);

  // ── Stats ──
  const [totalCount, setTotalCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [approvedCount, setApprovedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);

  // ══════════════════════════════════════════════════════
  // API — viewAllTopups
  // ══════════════════════════════════════════════════════
  const viewAllTopups = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/topup/viewAllTopups`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.error) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setTopupList(response.data.topups);
          setTotalCount(response.data.topups.length);
          setPendingCount(
            response.data.topups.filter((t) => t.status === "pending").length,
          );
          setApprovedCount(
            response.data.topups.filter((t) => t.status === "approved").length,
          );
          setRejectedCount(
            response.data.topups.filter((t) => t.status === "rejected").length,
          );
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load topup requests.",
        });
      });
  };

  // ══════════════════════════════════════════════════════
  // API — updateTopupStatus (approve/reject)
  // ══════════════════════════════════════════════════════
  const updateTopupStatus = (topup, status) => {
    const formData = new FormData();
    formData.append("id", topup.id);
    formData.append("status", status);

    axios
      .post(`${baseUrl}/admin/topup/updateTopupStatus`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: `Topup request ${status} successfully!`,
          });
          viewAllTopups();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.message,
          });
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/admin/login");
      return;
    }
    viewAllTopups();
  }, [authToken]);

  // ── Filter ──
  const filteredList = topupList.filter((t) => {
    const matchSearch =
      t.kmdeliveryman?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.kmdeliveryman?.phone?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchTab = activeTab === "all" || t.status === "pending";
    return matchSearch && matchTab;
  });

  // ── Actions ──
  const getActions = (topup) => {
    const actions = [
      {
        label: "View Details",
        onClick: () => {
          setSelectedTopup(topup);
          setShowViewModal(true);
          setOpenActionMenu(null);
        },
      },
    ];

    if (topup.status === "pending") {
      actions.push(
        {
          label: "Approve",
          onClick: () => {
            updateTopupStatus(topup, "approved");
            setOpenActionMenu(null);
          },
          variant: "success",
        },
        {
          label: "Reject",
          onClick: () => {
            updateTopupStatus(topup, "rejected");
            setOpenActionMenu(null);
          },
          variant: "danger",
        },
      );
    }

    return actions;
  };

  // ── Stats ──
  const statsData = [
    {
      icon: FaMoneyBillWave,
      iconBg: "linear-gradient(135deg,#e3f2fd,#bbdefb)",
      iconColor: "#2196f3",
      label: "Total Requests",
      value: totalCount.toString(),
      badge: "All",
    },
    {
      icon: FaClock,
      iconBg: "linear-gradient(135deg,#fff3e0,#ffe0b2)",
      iconColor: "#ff9800",
      label: "Pending",
      value: pendingCount.toString(),
      badge: "Pending",
    },
    {
      icon: FaCheckCircle,
      iconBg: "linear-gradient(135deg,#e8f5f0,#d4ede3)",
      iconColor: "#0ac46c",
      label: "Approved",
      value: approvedCount.toString(),
      badge: "Approved",
    },
    {
      icon: FaTimesCircle,
      iconBg: "linear-gradient(135deg,#ffebee,#ffcdd2)",
      iconColor: "#f44336",
      label: "Rejected",
      value: rejectedCount.toString(),
      badge: "Rejected",
    },
  ];

  const getStatusBadge = (status) => {
    const map = {
      approved: "ksa-tp-badge-approved",
      pending: "ksa-tp-badge-pending",
      rejected: "ksa-tp-badge-rejected",
    };
    const labels = {
      approved: "Approved",
      pending: "Pending",
      rejected: "Rejected",
    };
    return (
      <span className={`ksa-tp-badge ${map[status] || map["pending"]}`}>
        {labels[status] || "Pending"}
      </span>
    );
  };

  return (
    <AdminLayout>
      {loading ? (
        <DeliveryTopupSkeleton />
      ) : (
      <>
      <div className="ksa-tp-container">
        {/* ── Header ── */}
        <div className="ksa-tp-header">
          <div>
            <h1>Topup Management</h1>
            <p>
              Delivery boys ki topup requests review karein aur approve/reject
              karein
            </p>
          </div>
        </div>

        {/* ── Stats ── */}
        <Row className="g-4 mb-4">
          {statsData.map((stat, idx) => (
            <Col key={idx} xs={12} sm={6} lg={3}>
              <StatCard {...stat} />
            </Col>
          ))}
        </Row>

        {/* ── Tabs ── */}
        <div className="ksa-tp-tabs">
          <button
            className={`ksa-tp-tab ${activeTab === "all" ? "ksa-tp-tab--active" : ""}`}
            onClick={() => {
              setActiveTab("all");
              setCurrentPage(1);
            }}
          >
            All Requests
          </button>
          <button
            className={`ksa-tp-tab ${activeTab === "pending" ? "ksa-tp-tab--active" : ""}`}
            onClick={() => {
              setActiveTab("pending");
              setCurrentPage(1);
            }}
          >
            Pending Requests
            {pendingCount > 0 && (
              <span className="ksa-tp-tab-badge">{pendingCount}</span>
            )}
          </button>
        </div>

        {/* ── Table ── */}
        <div className="ksa-tp-table-section">
          <div className="ksa-tp-table-header">
            <div className="ksa-tp-search">
              <FaSearch size={14} />
              <input
                type="text"
                placeholder="Search by delivery man name or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="ksa-tp-table-wrapper">
            <table className="ksa-tp-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Delivery Man</th>
                  <th>Phone</th>
                  <th>Amount</th>
                  <th>Receipt</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="ksa-tp-empty">
                      No topup requests found
                    </td>
                  </tr>
                ) : (
                  filteredList
                    .slice(
                      (currentPage - 1) * itemsPerPage,
                      currentPage * itemsPerPage,
                    )
                    .map((t, i) => (
                      <tr key={t.id}>
                        <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                        <td className="ksa-tp-name">
                          {t.kmdeliveryman?.image ? (
                            <img
                              src={`${baseUrlForFiles}/${t.kmdeliveryman.image}`}
                              alt={t.kmdeliveryman?.name}
                              className="ksa-tp-avatar"
                            />
                          ) : (
                            <div className="ksa-tp-avatar-placeholder">
                              {t.kmdeliveryman?.name?.charAt(0)}
                            </div>
                          )}
                          {t.kmdeliveryman?.name || "—"}
                        </td>
                        <td>{t.kmdeliveryman?.phone || "—"}</td>
                        <td>Rs. {t.amount}</td>
                        <td>
                          {t.receipt_image ? (
                            <a
                              href={`${baseUrlForFiles}/${t.receipt_image}`}
                              target="_blank"
                              rel="noreferrer"
                              className="ksa-tp-doc-link"
                            >
                              View Receipt
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>{getStatusBadge(t.status)}</td>
                        <td>
                          <ActionDropdown
                            isOpen={openActionMenu === t.id}
                            onToggle={() =>
                              setOpenActionMenu(
                                openActionMenu === t.id ? null : t.id,
                              )
                            }
                            actions={getActions(t)}
                          />
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            totalItems={filteredList.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => {
              setItemsPerPage(n);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      {/* ── View Modal ── */}
      {/* ── View Modal ── */}
      {showViewModal && selectedTopup && (
        <div
          className="ksa-modal-overlay"
          onClick={() => setShowViewModal(false)}
        >
          <div
            className="ksa-modal ksa-modal-lg ksa-category-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ksa-category-modal-header">
              <h3 className="ksa-category-modal-title">
                Topup Request Details
              </h3>
              <button
                className="ksa-modal-close"
                onClick={() => setShowViewModal(false)}
              >
                <FaTimes size={18} />
              </button>
            </div>

            <div className="ksa-category-modal-body">
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <div className="ksa-tp-detail-group">
                    <span className="ksa-tp-detail-label">Delivery Man</span>
                    <p className="ksa-tp-detail-value">
                      {selectedTopup.kmdeliveryman?.name || "—"}
                    </p>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="ksa-tp-detail-group">
                    <span className="ksa-tp-detail-label">Phone</span>
                    <p className="ksa-tp-detail-value">
                      {selectedTopup.kmdeliveryman?.phone || "—"}
                    </p>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="ksa-tp-detail-group">
                    <span className="ksa-tp-detail-label">Amount</span>
                    <p className="ksa-tp-detail-value">
                      Rs. {selectedTopup.amount}
                    </p>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="ksa-tp-detail-group">
                    <span className="ksa-tp-detail-label">Status</span>
                    <p className="ksa-tp-detail-value">
                      {getStatusBadge(selectedTopup.status || "pending")}
                    </p>
                  </div>
                </Col>
                {selectedTopup.receipt_image && (
                  <Col xs={12}>
                    <div className="ksa-tp-detail-group">
                      <span className="ksa-tp-detail-label">Receipt</span>
                      <img
                        src={`${baseUrlForFiles}/${selectedTopup.receipt_image}`}
                        alt="Receipt"
                        className="ksa-tp-receipt-preview"
                      />
                    </div>
                  </Col>
                )}
              </Row>
            </div>

            <div className="ksa-category-modal-footer">
              <button
                className="outline-gradient"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
              {selectedTopup.status === "pending" && (
                <>
                  <button
                    className="ksa-tp-reject-btn"
                    onClick={() => {
                      updateTopupStatus(selectedTopup, "rejected");
                      setShowViewModal(false);
                    }}
                  >
                    Reject
                  </button>
                  <button
                    className="ksa-tp-approve-btn"
                    onClick={() => {
                      updateTopupStatus(selectedTopup, "approved");
                      setShowViewModal(false);
                    }}
                  >
                    Approve
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </AdminLayout>
  );
};

export default DeliveryTopup;
